import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { HelpCircle } from 'lucide-react';
import { EditorSheet } from '@/components/app-editor/editor-sheet';
import { useChatwootWidget } from '@/components/chatwoot-widget/chatwoot-widget-context';
import { useI18nContext } from '@/i18n/i18n-react';
import type { SheetSnap } from '@/lib/app-editor/editor-sheet';
import { cn } from '@/lib/utils';
import { KorzaStudioBackdrop } from '@/pages/onboarding/mockup/korza-studio-backdrop';
import { MockupFramedByCanvasContext } from '@/pages/onboarding/mockup/mockup-canvas-context';
import { ZOOM_TRANSITION_DURATION_MS } from '@/pages/onboarding/mockup/zoomed-iphone-mockup';
import {
  isKeyboardOpen,
  MOCKUP_FRAME_SIZE,
  type MockupFocus,
  placeMockup,
  STUDIO_SHEET_RATIO,
} from '@/pages/onboarding/mobile-studio-geometry';
import { OnboardingAccountActions } from '@/pages/onboarding/onboarding-account-actions';

// Same frosted glass as KorzaStudioBadge, for the controls over the canvas.
const FROSTED =
  'rounded-full bg-white/70 shadow-sm ring-1 ring-black/5 backdrop-blur-md';

/**
 * Where OnboardingPageFooter sits: `pinned` to the bottom of the mobile
 * Studio's sheet, `inline` at the end of it while the keyboard is open, `null`
 * outside the sheet (desktop, or a step without the mobile mockup).
 */
export type OnboardingFooterPlacement = 'pinned' | 'inline' | null;

const FooterPlacementContext = createContext<OnboardingFooterPlacement>(null);

export function useOnboardingFooterPlacement(): OnboardingFooterPlacement {
  return useContext(FooterPlacementContext);
}

interface VisualViewportBox {
  top: number;
  height: number;
  keyboardOpen: boolean;
}

function readVisualViewportBox(): VisualViewportBox | null {
  const viewport = window.visualViewport;
  if (!viewport) return null;
  return {
    top: viewport.offsetTop,
    height: viewport.height,
    keyboardOpen: isKeyboardOpen({
      visualHeight: viewport.height,
      layoutHeight: document.documentElement.clientHeight,
    }),
  };
}

/**
 * The visual viewport, which iOS Safari shrinks for the keyboard (the layout
 * viewport and `dvh` stay the same, so the keyboard would cover the sheet).
 * Read on the first render too: the layout must not move after it.
 */
function useVisualViewportBox(): VisualViewportBox | null {
  const [box, setBox] = useState(readVisualViewportBox);
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const update = () => setBox(readVisualViewportBox());
    update();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
    };
  }, []);
  return box;
}

/**
 * Size of the element, re-read after every render as well as on resize: a
 * layout change made by the same render (the sheet appearing, the keyboard)
 * is then picked up before the browser paints, not a frame later through
 * ResizeObserver — where Safari's view transition snapshots would catch the
 * phone framed for the old size, then see it jump.
 */
function useElementSize<T extends HTMLElement>(
  initial = { width: 0, height: 0 }
) {
  const ref = useRef<T>(null);
  const [size, setSize] = useState(initial);
  // No dependency list on purpose (every render); setSize bails out when
  // the size is unchanged, so it cannot loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const rect = element.getBoundingClientRect();
    setSize((current) =>
      current.width === rect.width && current.height === rect.height
        ? current
        : { width: rect.width, height: rect.height }
    );
  });
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, size] as const;
}

/**
 * The mockup at its desktop size, scaled as a whole: the entire phone fitted
 * to the canvas, or magnified around the step's `focus` (the canvas clips the
 * rest). Only a change of focus animates, like ZoomedIPhoneMockup's zoom: the
 * canvas resizing (the sheet appearing, the keyboard) moves the phone at once,
 * or it would zoom out and back in on every step.
 */
function FramedMockup({
  focus,
  children,
}: {
  focus?: MockupFocus;
  children: ReactNode;
}) {
  const [ref, size] = useElementSize<HTMLDivElement>();
  const { scale, x, y } = placeMockup(size, focus);
  const focusKey = focus ? `${focus.x},${focus.y},${focus.zoom}` : 'whole';
  const [lastFocusKey, setLastFocusKey] = useState(focusKey);
  const [zooming, setZooming] = useState(false);
  if (focusKey !== lastFocusKey) {
    setLastFocusKey(focusKey);
    setZooming(true);
  }
  useEffect(() => {
    if (!zooming) return;
    const timeout = window.setTimeout(
      () => setZooming(false),
      ZOOM_TRANSITION_DURATION_MS
    );
    return () => window.clearTimeout(timeout);
  }, [zooming, lastFocusKey]);

  return (
    <div ref={ref} className="absolute inset-0">
      {scale > 0 && (
        <div
          data-mockup-frame
          className={cn(
            'absolute left-0 top-0 z-10 origin-top-left',
            zooming &&
              'transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none'
          )}
          style={{
            width: MOCKUP_FRAME_SIZE.width,
            transform: `translate(${x}px, ${y}px) scale(${scale})`,
          }}
        >
          <MockupFramedByCanvasContext.Provider value>
            {children}
          </MockupFramedByCanvasContext.Provider>
        </div>
      )}
    </div>
  );
}

const EDITABLE_SELECTOR = 'input, textarea, [contenteditable="true"]';

// EditorSheet animates its height over 700ms here (heightTransitionClassName).
const SHEET_HEIGHT_TRANSITION_MS = 750;

// EditorSheet's handle (h-6) above the content.
const SHEET_HANDLE_HEIGHT = 24;

export interface OnboardingMobileStudioProps {
  children: ReactNode;
  mockup: ReactNode;
  /** What the canvas frames; the whole phone when absent. */
  focus?: MockupFocus;
  /**
   * Opens the sheet only as tall as the form (up to the usual height), for
   * a short form that would leave it half empty. Turned on after arriving
   * and off before leaving, so the page transitions still match.
   */
  fitSheet?: boolean;
  className?: string;
}

/**
 * Below `lg`: the Korza Studio on top and the form in the editors' bottom
 * sheet (EditorSheet, starting at the medium height), like the welcome
 * notification editor. Unlike the editors, the whole layout follows the
 * visual viewport, so with the keyboard open the phone shrinks but stays on
 * screen while the merchant types. No "Studio" badge here: at this size it
 * would cover the phone. Help moves from the floating button (which would
 * cover "Voltar") to the canvas corner.
 */
export function OnboardingMobileStudio({
  children,
  mockup,
  focus,
  fitSheet = false,
  className,
}: OnboardingMobileStudioProps) {
  const { LL } = useI18nContext();
  const { setHasShellOnScreen, setIsOpen } = useChatwootWidget();
  const viewport = useVisualViewportBox();
  // Window height until measured, so the sheet is there on the first render.
  const [bodyRef, bodySize] = useElementSize<HTMLDivElement>({
    width: window.innerWidth,
    height: viewport?.height ?? window.innerHeight,
  });
  const [snap, setSnap] = useState<SheetSnap>('medium');
  const [contentRef, contentSize] = useElementSize<HTMLDivElement>();
  const sheetRatio =
    fitSheet && contentSize.height > 0 && bodySize.height > 0
      ? Math.min(
          STUDIO_SHEET_RATIO,
          (SHEET_HANDLE_HEIGHT + contentSize.height) / bodySize.height
        )
      : STUDIO_SHEET_RATIO;
  const keyboardOpen = viewport?.keyboardOpen ?? false;

  useEffect(() => {
    setHasShellOnScreen(true);
    return () => setHasShellOnScreen(false);
  }, [setHasShellOnScreen]);

  // Once the layout has shrunk above the keyboard, bring the field back into
  // the (now shorter) sheet, when the sheet's height transition ends (the
  // timeout covers reduced motion, where there is no transition).
  useEffect(() => {
    const body = bodyRef.current;
    if (!keyboardOpen || !body) return;
    const centerActiveField = () => {
      const active = document.activeElement;
      if (
        active instanceof HTMLElement &&
        active.matches(EDITABLE_SELECTOR) &&
        body.contains(active)
      ) {
        active.scrollIntoView?.({ block: 'center' });
      }
    };
    const onTransitionEnd = (e: TransitionEvent) => {
      if (e.propertyName === 'height') centerActiveField();
    };
    body.addEventListener('transitionend', onTransitionEnd);
    const timeout = window.setTimeout(
      centerActiveField,
      SHEET_HEIGHT_TRANSITION_MS
    );
    return () => {
      body.removeEventListener('transitionend', onTransitionEnd);
      window.clearTimeout(timeout);
    };
  }, [keyboardOpen, bodySize.height, bodyRef]);

  return (
    <div
      ref={bodyRef}
      className={cn('flex h-dvh w-full flex-col overflow-hidden', className)}
      style={
        viewport
          ? {
              position: 'fixed',
              left: 0,
              right: 0,
              top: viewport.top,
              height: viewport.height,
            }
          : undefined
      }
    >
      <div className="relative min-h-0 flex-1 overflow-hidden">
        <KorzaStudioBackdrop />
        <FramedMockup focus={focus}>{mockup}</FramedMockup>
        <OnboardingAccountActions
          compact
          className={cn('absolute left-3 top-3 z-30', FROSTED)}
        />
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          aria-label={LL.common.help()}
          title={LL.common.help()}
          className={cn(
            'absolute right-3 top-3 z-30 flex size-10 items-center justify-center text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            FROSTED
          )}
        >
          <HelpCircle className="size-5" />
        </button>
      </div>
      <EditorSheet
        snap={snap}
        onSnapChange={setSnap}
        bodyHeight={bodySize.height}
        handleLabel={LL.appEditor.sheet.handle()}
        growOnFocus={false}
        mediumRatio={sheetRatio}
        heightTransitionClassName="duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        header={null}
      >
        <FooterPlacementContext.Provider
          value={keyboardOpen ? 'inline' : 'pinned'}
        >
          <div
            ref={contentRef}
            className="mx-auto flex w-full max-w-md flex-col px-5 pt-2"
          >
            {children}
          </div>
        </FooterPlacementContext.Provider>
      </EditorSheet>
    </div>
  );
}
